import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PostAddIcon from '@mui/icons-material/PostAdd';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import prettyMilliseconds from 'pretty-ms';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AxiosConfig from '../../../../../config/AxiosConfig';
import b64toBlob from '../../../../helpers/utils/blob';
import { setSnackBar } from '../../../../states/app/staticSlice';
import PhonecallIcon from '../../../utils/common/PhonecallIcon';

export const TicketActivityPhonecall = ({
  phonecall,
  ticket,
  mutate,
  mutateTicket,
}) => {
  const profile = useSelector((state) => state?.login?.profile);
  const _theme = useTheme();
  const dispatch = useDispatch();
  const [displayNoteContentEditor, setDisplayNoteContentEditor] =
    useState(false);
  const [phonecallNoteContent, setPhonecallNoteContent] = useState(
    phonecall?.note?.content || ''
  );
  const [originalTranscript, setOriginalTranscript] = useState(
    phonecall?.transcript?.summary || ''
  );
  const [translatedTranscript, setTranslatedTranscript] = useState(null);
  const [transcriptToShow, setTranscriptToShow] = useState(originalTranscript);
  const [isTranslated, setIsTranslated] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(
    phonecall?.recordingConsent
  );
  const isFrenchInitial =
    phonecall?.transcript?.language?.toLowerCase() === 'french';

  useEffect(() => {
    setOriginalTranscript(phonecall?.transcript?.summary);
    setTranscriptToShow(phonecall?.transcript?.summary);
  }, [phonecall?.transcript]);

  const handleCopy = () => {
    navigator.clipboard.writeText(transcriptToShow);
  };
  const handlePhonecallNoteContentChange = (e) => {
    setPhonecallNoteContent(e.target.value);
  };

  const handleWriteNoteToPhonecall = (_e) => {
    setDisplayNoteContentEditor(true);
  };

  const handleConfirmAddNoteToPhonecall = (_e) => {
    setDisplayNoteContentEditor(false);

    AxiosConfig.create(
      phonecall?.note ? 'put' : 'post',
      `/phonecalls/${phonecall?._id}/handleNote${
        phonecall?.note ? `/${phonecall?.note?._id}` : ''
      }`
    )
      .call({
        content: phonecallNoteContent,
        userId: profile?._id,
      })
      .exit((_res) => {
        mutate();
        mutateTicket();
      })
      .catch((err) => console.error(err));
  };

  const handleTranslate = async () => {
    if (!isTranslated && !translatedTranscript && !isFrenchInitial) {
      // Si ce n'est pas traduit, qu'on n'a pas de traduction en cache, et que la langue initiale n'est pas le français, on traduit
      const targetCode = 'fr'; // Traduire vers le français
      try {
        const translationResult = await AxiosConfig.create(
          'post',
          `/translate/text`
        )
          .call({
            text: originalTranscript,
            target: targetCode,
          })
          .exit((res) => {
            if (res && res.status > 300) {
              const messageError = `Error: ${res?.status} - ${res?.statusText}: Translate Text`;
              dispatch(
                setSnackBar({
                  open: true,
                  message: messageError,
                  color: 'error',
                })
              );
            } else {
              dispatch(
                setSnackBar({ open: false, message: null, color: null })
              );
            }
            return res?.data?.data || null;
          });
        if (translationResult) {
          setTranslatedTranscript(translationResult);
          setTranscriptToShow(translationResult);
          setIsTranslated(true);
        }
      } catch (error) {
        console.error('Erreur lors de la traduction:', error);
        // Gérer l'erreur ici (afficher un message à l'utilisateur, etc.)
      }
    } else {
      // Si c'est déjà traduit, qu'on a une traduction en cache, ou que la langue initiale est le français, on bascule ou on affiche l'original
      setTranscriptToShow(
        isTranslated || isFrenchInitial
          ? originalTranscript
          : translatedTranscript
      );
      setIsTranslated((state) => !state);
    }
  };

  const downloadTranscript = (content) => {
    const link = document.createElement('a');
    const blobContent = b64toBlob(content, 'application/pdf');
    const blobUrl = URL.createObjectURL(blobContent);
    link.setAttribute(
      'download',
      `Transcript-call-${phonecall?.sipId}-${phonecall?.startedAt?.toLocaleString('fr')}.txt`
    );
    link.href = `${blobUrl}`;
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handleCancelAddNoteToPhonecall = (_e) => {
    setDisplayNoteContentEditor(false);
  };

  const isExpired = () => {
    const phonecallDate = new Date(phonecall?.createdAt);
    phonecallDate.setDate(phonecallDate.getDate() + 7);
    return new Date() > phonecallDate;
  };

  const handleConsentChange = async (consent) => {
    try {
      setConsentLoading(true);
      await AxiosConfig.create('put', `/transcripts/${phonecall?._id}/consent`)
        .call({
          recordingConsent: consent,
        })
        .getResponseData();
      mutateTicket();
      setRecordingConsent(consent);
    } catch (error) {
      setRecordingConsent(phonecall?.recordingConsent);
      console.error('Erreur lors de la mise à jour du consentement:', error);
    } finally {
      setConsentLoading(false);
    }
  };

  return (
    <Paper
      key={phonecall?._id}
      sx={{
        padding: 1,
        marginBottom: 1,
        borderRadius: '8px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          // justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <PhonecallIcon />
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              // gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                fontWeight="bold"
                variant="body"
              >{`${phonecall?.handledBy?.firstName} ${phonecall?.handledBy?.lastName} > ${ticket?.contact?.firstName} ${ticket?.contact?.lastName}`}</Typography>
              <Typography component={'span'} variant="body2">
                {phonecall?.externalNumber}
              </Typography>
            </Box>
            <Typography component={'span'} variant="body">
              {new Date(phonecall?.startedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              // gap: 1,
            }}
          >
            <Typography variant="body1">
              Appel{' '}
              {phonecall?.status === 'Terminé'
                ? phonecall?.type.toLowerCase()
                : phonecall?.status}
            </Typography>
            {phonecall?.duration ? (
              <Typography component={'span'} variant="body2">
                {prettyMilliseconds(phonecall?.duration * 1000 || 0, {
                  verbose: true,
                })}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 1, border: 1 }} />
      {!isExpired() && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              style={{
                fontSize: '1em',
                marginBottom: 1,
              }}
            >
              Consentement à l&apos;enregistrement de l&apos;appel
            </Typography>
            <ButtonGroup size="small" aria-label="Small button group">
              <Button
                loading={consentLoading}
                size="small"
                disabled={consentLoading}
                key="one"
                variant={recordingConsent ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => handleConsentChange(true)}
              >
                Oui
              </Button>
              <Button
                loading={consentLoading}
                size="small"
                disabled={consentLoading}
                key="two"
                variant={recordingConsent ? 'outlined' : 'contained'}
                color="primary"
                onClick={() => handleConsentChange(false)}
              >
                Non
              </Button>
            </ButtonGroup>
            <Typography variant="caption">
              Vous pouvez modifier le consentement à l&apos;enregistrement de
              l&apos;appel jusqu&apos;à 7 jours après l&apos;appel.
            </Typography>
          </Box>
          <Divider sx={{ my: 1, border: 1 }} />
        </>
      )}
      {/* Résumé de l'appel et transcript*/}
      {phonecall?.recordingConsent === true && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              direction: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography color="primary">Résumé de l&apos;appel:</Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Button
                color="primary"
                size="small"
                startIcon={<TranslateOutlinedIcon />}
                onClick={() => handleTranslate()}
              >
                {isTranslated ? 'Revenir au message original' : 'Traduire'}
              </Button>
              <Button
                color="primary"
                size="small"
                startIcon={<ContentCopyOutlinedIcon />}
                onClick={handleCopy}
              >
                Copier
              </Button>
            </Box>
          </Box>
          <Typography sx={{ mt: 2, whiteSpace: 'pre-line' }}>
            {transcriptToShow || 'Chargement de la transcription en cours...'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={!phonecall?.transcript?.base64EncodedContent}
              startIcon={<ArrowDownwardOutlinedIcon />}
              onClick={() =>
                downloadTranscript(phonecall?.transcript?.base64EncodedContent)
              }
            >
              Télécharger la transcription
            </Button>
          </Box>
          <Divider sx={{ my: 1 }} />
        </Box>
      )}
      {/* BUTTON to write note to phonecall */}
      {!phonecall?.note && !displayNoteContentEditor ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            padding: 0,
            margin: 0,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PostAddIcon />}
            onClick={handleWriteNoteToPhonecall}
          >
            Ajouter une note
          </Button>
        </Box>
      ) : null}
      {/* Content editor to add note on confirmation */}
      {displayNoteContentEditor ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            padding: 0,
            margin: 0,
          }}
        >
          <TextField
            id="phonecall-note"
            placeholder="Rédiger la note ici..."
            multiline
            sx={{ width: '100%', padding: 1 }}
            rows={3}
            value={phonecallNoteContent}
            onChange={handlePhonecallNoteContentChange}
            variant="filled"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <IconButton
              size="large"
              color="success"
              onClick={handleConfirmAddNoteToPhonecall}
            >
              <CheckCircleIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              size="large"
              color="error"
              onClick={handleCancelAddNoteToPhonecall}
            >
              <CancelIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Box>
      ) : null}
      {/* Display note, allow to modify it */}
      {phonecall?.note && !displayNoteContentEditor ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography component={'span'} sx={{ whiteSpace: 'pre-wrap' }}>
            {phonecall?.note?.user?.firstName} {phonecall?.note?.user?.lastName}{' '}
            : {phonecall?.note?.content}
          </Typography>
          <IconButton
            size="large"
            color="primary"
            onClick={handleWriteNoteToPhonecall}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Box>
      ) : null}
    </Paper>
  );
};

export default TicketActivityPhonecall;
